// ============================================================================
// checklistDefinitionService — CRUD on the `checklists` collection.
// ----------------------------------------------------------------------------
// A ChecklistDefinition holds the CONFIG of a checklist — title, form
// attachments, criteria, proof rules, dependencies, assignee, checklistType
// and its parent Timeline container. No canvas position, no per-day state;
// those live on the book's page/layout_version docs (see checklistBookService
// and layoutVersionService).
//
// Every create/update also:
//   1. Ensures the book for this workspace + type exists (bootstrap safety).
//   2. When a new checklist is created, adds a node entry to the CURRENT
//      layout version and appends the checklist to today's page (empty
//      completion state). Layout version bump forward-propagates via
//      layoutVersionService.applyEditToPage.
//   3. Emits a CHECKLIST_* activity ledger event on `workspace_activity`.
//
// See src/types/checklistBooks.ts for the data shapes and rationale.
// ============================================================================

import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Firestore,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { recordWorkspaceActivity, type WorkspaceActivityAction } from './activityService'
import { scopedReadConstraints } from './accessScopeService'
import { findEmptyCanvasSlot, toSlotBox } from './canvasPlacementService'
import { NODE_HEIGHT, NODE_WIDTH } from './canvasLayoutConstants'
import {
  checklistPath,
  type BookId,
  type ChecklistDefinition,
  type ChecklistType,
} from '../types/checklistBooks'
import { updateChecklistWorkflowStatus } from './checklistStatusWriter'
import { applyStripboardDelta } from './stripboardDeltaService'
import { nextStatusAfterAssignment } from './checklistStatusMachine'
import type { BoardStatus, ChecklistWorkflowStatus } from '../types/schema'
import {
  ensurePageForKey,
  listPages,
  pageKeyFor,
  setChecklistCompletionOnPage,
  removeChecklistFromPage,
  type BookActor,
} from './checklistBookService'
import { applyEditToPage, getLayoutVersion, type LayoutActor } from './layoutVersionService'
import { invalidateChecklistDefinitionListCache } from './checklistReadCache'

// ---------------------------------------------------------------------------
// Actor
// ---------------------------------------------------------------------------

export type DefinitionActor = LayoutActor & BookActor

// ---------------------------------------------------------------------------
// Refs
// ---------------------------------------------------------------------------

const definitionRef = (checklistId: string) =>
  doc(firestoreDb, checklistPath(checklistId))


// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export const getDefinition = async (
  checklistId: string,
): Promise<ChecklistDefinition | null> => {
  const snap = await getDoc(definitionRef(checklistId))
  return snap.exists() ? (snap.data() as ChecklistDefinition) : null
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export const updateDefinition = async (
  checklistId: string,
  patch: Partial<ChecklistDefinition>,
  actor: DefinitionActor,
  opts: {
    action?: ChecklistCanonicalActivityAction
    summary?: string
    metadata?: Record<string, unknown>
  } = {},
): Promise<void> => {
  const existing = await getDefinition(checklistId)
  if (!existing) {
    throw new Error(`updateDefinition: checklist ${checklistId} not found`)
  }
  await updateDoc(definitionRef(checklistId), {
    ...patch,
    updatedAt: serverTimestamp(),
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
    recordVersion: (existing.recordVersion ?? 0) + 1,
  })
  invalidateChecklistDefinitionListCache(existing.workspaceId, existing.checklistType)
  if (patch.checklistType && patch.checklistType !== existing.checklistType) {
    invalidateChecklistDefinitionListCache(existing.workspaceId, patch.checklistType)
  }
  await emitChecklistActivity(firestoreDb, {
    workspaceId: existing.workspaceId,
    actor,
    targetId: existing.id,
    targetTitle: patch.title ?? existing.title,
    action: opts.action ?? 'CHECKLIST_UPDATED',
    summary: opts.summary ?? `Updated checklist ${existing.title}`,
    metadata: opts.metadata,
  })
}

/**
 * Assign / unassign. When assigning fresh (no prior workflow status), also
 * promotes workflowStatus to 'ASSIGNED' on TODAY'S PAGE for immediate
 * visibility on the assignee's Todos surface. Historical pages are untouched.
 */
export const assignDefinition = async (
  checklistId: string,
  assignee: { userId: string; displayName: string } | null,
  actor: DefinitionActor,
  opts: { skipStatusPromotion?: boolean } = {},
): Promise<void> => {
  const existing = await getDefinition(checklistId)
  if (!existing) throw new Error(`assignDefinition: checklist ${checklistId} not found`)

  await updateDoc(definitionRef(checklistId), {
    assigneeId: assignee?.userId ?? null,
    assigneeName: assignee?.displayName ?? null,
    ...(assignee?.userId ? { assigneeHistoryIds: arrayUnion(assignee.userId) } : {}),
    updatedAt: serverTimestamp(),
    lastUpdatedBy: actor.uid,
    lastUpdatedByName: actor.displayName ?? null,
    recordVersion: (existing.recordVersion ?? 0) + 1,
  })
  invalidateChecklistDefinitionListCache(existing.workspaceId, existing.checklistType)

  await emitChecklistActivity(firestoreDb, {
    workspaceId: existing.workspaceId,
    actor,
    targetId: existing.id,
    targetTitle: existing.title,
    action: 'CHECKLIST_ASSIGNED',
    summary: assignee ? `Assigned to ${assignee.displayName}` : 'Removed the assignee',
    metadata: {
      previousAssigneeId: existing.assigneeId ?? null,
      assigneeId: assignee?.userId ?? null,
    },
  })

  if (opts.skipStatusPromotion) return

  try {
    const rawDef = existing as ChecklistDefinition & {
      itemId?: string
      linkedItemId?: string
      checklistItemId?: string
    }
    let linkedItem: {
      id: string
      workflowStatus?: ChecklistWorkflowStatus | null
      boardStatus?: BoardStatus | null
      recordVersion?: number
      workspaceId?: string
      checklistType?: ChecklistType
    } | null = null

    // 1. Direct linked item id on the definition doc if present
    const explicitItemId =
      (typeof rawDef.itemId === 'string' && rawDef.itemId) ||
      (typeof rawDef.linkedItemId === 'string' && rawDef.linkedItemId) ||
      (typeof rawDef.checklistItemId === 'string' && rawDef.checklistItemId) ||
      null

    if (explicitItemId) {
      const itemSnap = await getDoc(doc(firestoreDb, 'checklists', explicitItemId))
      if (itemSnap.exists()) {
        const data = itemSnap.data() as Record<string, unknown>
        linkedItem = {
          id: itemSnap.id,
          workflowStatus: (data.workflowStatus as ChecklistWorkflowStatus | null | undefined) ?? null,
          boardStatus: (data.boardStatus as BoardStatus | null | undefined) ?? null,
          recordVersion: typeof data.recordVersion === 'number' ? data.recordVersion : 1,
          workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : existing.workspaceId,
          checklistType: (data.checklistType as ChecklistType | undefined) ?? existing.checklistType,
        }
      }
    } else {
      // 2. Query checklists where definitionId === checklistId
      const q = query(
        collection(firestoreDb, 'checklists'),
        where('workspaceId', '==', existing.workspaceId),
        where('definitionId', '==', checklistId),
        ...scopedReadConstraints(existing.workspaceId),
      )
      const querySnap = await getDocs(q)
      if (!querySnap.empty) {
        const firstDoc = querySnap.docs[0]
        const data = firstDoc.data() as Record<string, unknown>
        linkedItem = {
          id: firstDoc.id,
          workflowStatus: (data.workflowStatus as ChecklistWorkflowStatus | null | undefined) ?? null,
          boardStatus: (data.boardStatus as BoardStatus | null | undefined) ?? null,
          recordVersion: typeof data.recordVersion === 'number' ? data.recordVersion : 1,
          workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : existing.workspaceId,
          checklistType: (data.checklistType as ChecklistType | undefined) ?? existing.checklistType,
        }
      }
    }

    if (linkedItem) {
      const nextStatus = nextStatusAfterAssignment(
        linkedItem.workflowStatus,
        Boolean(assignee?.userId),
      )
      if (nextStatus !== null) {
        await updateChecklistWorkflowStatus({
          item: {
            id: linkedItem.id,
            boardStatus: linkedItem.boardStatus ?? null,
            recordVersion: linkedItem.recordVersion ?? 1,
            workspaceId: linkedItem.workspaceId ?? existing.workspaceId,
            workflowStatus: linkedItem.workflowStatus ?? null,
            checklistType: linkedItem.checklistType ?? existing.checklistType,
          },
          workflowStatus: nextStatus,
          runDelta: applyStripboardDelta,
          actor: { id: actor.uid, name: actor.displayName ?? 'System' },
        })
      }
    }
  } catch (error) {
    console.warn('[assignDefinition] linked item status auto-promotion failed:', error)
  }
}


// ---------------------------------------------------------------------------
// Layout ↔ definition sync helpers
// ---------------------------------------------------------------------------

/**
 * Append a node for `definition` to the book's current layout and seed today's
 * page completion entry (empty state).
 */
export const addChecklistToBookLayout = async (
  bookId: BookId,
  workspaceId: string,
  definition: ChecklistDefinition,
  position: { x: number; y: number },
  actor: DefinitionActor,
): Promise<void> => {
  const today = new Date()
  const type = definition.checklistType
  const pageKey = pageKeyFor(type, today)
  const anchor = await ensurePageForKey(bookId, workspaceId, type, pageKey, actor)

  // 1. Load current layout via the current page.
  const baseVersion = await getLayoutVersion(bookId, anchor.layoutVersionId)
  const baseNodes = baseVersion?.nodes ?? {}
  const baseWires = baseVersion?.wires ?? []

  // Hard-and-fast rule: every add-to-canvas path lands on empty space. The
  // caller's `position` is treated as a hint (drag drop, click-to-place);
  // `findEmptyCanvasSlot` returns it verbatim when it fits, otherwise snaps
  // to the nearest empty grid cell. This runs against every node currently
  // on the layout — checklists AND gates — so external creators
  // (BoardExplorer, QuickCreate, AI Line) no longer stack on (0, 0).
  const existingLayoutBoxes = Object.values(baseNodes).map((node) => toSlotBox({
    position: node.position,
    nodeKind: 'CHECKLIST',
  }))
  const resolvedPosition = findEmptyCanvasSlot({
    existing: existingLayoutBoxes,
    newNode: { width: NODE_WIDTH, height: NODE_HEIGHT },
    preferred: position,
  })

  const nextNodes = {
    ...baseNodes,
    [definition.id]: {
      checklistId: definition.id,
      position: resolvedPosition,
    },
  }

  await applyEditToPage(
    bookId,
    workspaceId,
    anchor.pageKey,
    { nodes: nextNodes, wires: baseWires },
    actor,
  )

  // 2. Seed empty completion entry on today's page only for INDIVIDUAL.
  if (type === 'INDIVIDUAL') {
    await setChecklistCompletionOnPage(
      bookId,
      pageKey,
      definition.id,
      { isCompleted: false, boardStatus: null, workflowStatus: 'CREATED' },
      actor,
    )
  }
}

/**
 * Remove a node for `checklistId` from the book's current layout and drop
 * its entry from today's page. Historical pages/layouts keep the record.
 */
export const removeChecklistFromBookLayout = async (
  bookId: BookId,
  workspaceId: string,
  checklistId: string,
  actor: DefinitionActor,
): Promise<void> => {
  const today = new Date()
  const pages = await listPages(bookId)
  if (pages.length === 0) return
  const anchorType: ChecklistType = pages[0].checklistType
  const pageKey = pageKeyFor(anchorType, today)
  const anchor = pages.find((p) => p.pageKey === pageKey) ?? pages[pages.length - 1]

  const baseVersion = await getLayoutVersion(bookId, anchor.layoutVersionId)
  if (!baseVersion) return

  const nextNodes = { ...baseVersion.nodes }
  delete nextNodes[checklistId]

  const nextWires = baseVersion.wires.filter(
    (w) => w.from !== checklistId && w.to !== checklistId,
  )

  await applyEditToPage(
    bookId,
    workspaceId,
    anchor.pageKey,
    { nodes: nextNodes, wires: nextWires },
    actor,
  )
  await removeChecklistFromPage(bookId, pageKey, checklistId, actor)
}

// ---------------------------------------------------------------------------
// Activity ledger
// ---------------------------------------------------------------------------

export const CHECKLIST_CANONICAL_ACTIVITY_ACTIONS = [
  'CHECKLIST_CREATED',
  'CHECKLIST_UPDATED',
  'CHECKLIST_STATUS_CHANGED',
  'CHECKLIST_COMPLETED',
  'CHECKLIST_REOPENED',
  'CHECKLIST_DELETED',
  'CHECKLIST_DEPENDENCY_CHANGED',
  'CHECKLIST_ASSIGNED',
  'CHECKLIST_PROOF_UPDATED',
  'CHECKLIST_FORCE_COMPLETED',
  'CHECKLIST_COMPLETION_BLOCKED',
] as const

export type ChecklistCanonicalActivityAction =
  (typeof CHECKLIST_CANONICAL_ACTIVITY_ACTIONS)[number]

const emitChecklistActivity = async (
  db: Firestore,
  input: {
    workspaceId: string
    actor: DefinitionActor
    targetId: string
    targetTitle: string
    action: ChecklistCanonicalActivityAction
    summary: string
    metadata?: Record<string, unknown>
    pageKey?: string
    bookId?: string
  },
) => {
  await recordWorkspaceActivity(db, {
    workspaceId: input.workspaceId,
    actorId: input.actor.uid,
    actorName: input.actor.displayName ?? null,
    targetType: 'CHECKLIST',
    targetId: input.targetId,
    targetTitle: input.targetTitle,
    action: input.action as WorkspaceActivityAction,
    summary: input.summary,
    metadata: {
      ...input.metadata,
      ...(input.pageKey ? { pageKey: input.pageKey } : {}),
      ...(input.bookId ? { bookId: input.bookId } : {}),
    },
  })
}
